/**
 * Query-string builder for the API's grid data source
 * (`GET <base>/grid?start&end&sortModel&filterModel` -> `{ rows, rowCount }`),
 * used by the legacy-style paged tables that fetch their own windows instead
 * of going through PalmettoDataGrid.
 */

export interface GridWindowArgs {
  /** 1-based page. */
  page: number;
  limit: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
  /** Exact-match filters (field -> value); empty/0/undefined values are skipped. */
  equals?: Record<string, string | number | null | undefined>;
  /**
   * Filter-drawer selections (field -> accepted values), sent as `isAnyOf` items:
   * a row matches when its field holds one of the values. Empty lists are skipped.
   */
  anyOf?: Record<string, (string | number)[]>;
  /**
   * Free-text search, sent as the API's quick filter: a row matches when one
   * of the endpoint's quick-filter columns contains it, and must also pass
   * `equals`. Trimmed; blank is skipped.
   */
  search?: string;
}

export interface GridWindow<T> {
  rows: T[];
  rowCount: number;
}

export const gridWindowQuery = ({
  page,
  limit,
  orderBy,
  order,
  equals,
  anyOf,
  search,
}: GridWindowArgs): string => {
  const start = Math.max(0, (page - 1) * limit);
  const params = new URLSearchParams();
  params.set('start', String(start));
  params.set('end', String(start + limit - 1));
  if (orderBy) params.set('sortModel', JSON.stringify([{ field: orderBy, sort: order || 'asc' }]));
  const items: { field: string; operator: string; value: unknown }[] = [
    ...Object.entries(equals || {})
      .filter(([, value]) => value !== undefined && value !== null && value !== '' && value !== 0)
      .map(([field, value]) => ({ field, operator: 'equals', value })),
    ...Object.entries(anyOf || {})
      .filter(([, values]) => Array.isArray(values) && values.length > 0)
      .map(([field, value]) => ({ field, operator: 'isAnyOf', value })),
  ];
  const term = search?.trim() ?? '';
  if (items.length || term)
    params.set(
      'filterModel',
      JSON.stringify(term ? { items, quickFilterValues: [term] } : { items }),
    );
  return `?${params.toString()}`;
};

export const pageCountOf = (rowCount: number, limit: number): number =>
  rowCount > 0 ? Math.ceil(rowCount / limit) : 0;
